/**
 * SMS campaign attribution for the Connect hub handoff (IMP-2303).
 *
 * Captures `?utm_source=sms` off the landing URL and persists it so a later
 * click into a community can still forward it to Connect hub. Connect hub's
 * own GA4 property reads the value from its landing URL; this app never
 * fires a GA4 event of its own.
 */

/** Per-tab. Cleared when the tab closes, which is the attribution window we want. */
const UTM_SOURCE_KEY = 'imentor.utm_source';

/** Allowlist. Anything else is dropped rather than persisted or forwarded. */
const ALLOWED_UTM_SOURCES = Object.freeze(['sms']);

/**
 * Normalizes a candidate utm_source value against the allowlist.
 *
 * @param {unknown} raw
 * @returns {string | null}
 */
export function normalizeUtmSource(raw) {
  if (typeof raw !== 'string') return null;
  const value = raw.trim().toLowerCase();
  return ALLOWED_UTM_SOURCES.includes(value) ? value : null;
}

/**
 * Reads `utm_source` off the landing URL and persists it for the tab.
 *
 * Call once, as early as possible (before the router mounts), so the value is
 * captured before anything discards the query string. A later load without
 * the param must not clear an earlier hit, so this only ever sets, never
 * clears, the stored value.
 *
 * @param {string} [search] - Defaults to the current location's query string.
 * @returns {string | null} The captured value, or null when absent/disallowed.
 */
export function captureUtmSource(search = window.location.search) {
  const value = normalizeUtmSource(new URLSearchParams(search).get('utm_source'));
  if (!value) return null;
  try {
    window.sessionStorage.setItem(UTM_SOURCE_KEY, value);
  } catch {
    // Storage blocked (e.g. Safari private mode). Attribution is best-effort
    // and must never break the app.
  }
  return value;
}

/**
 * Returns the persisted utm_source for this tab, or null.
 *
 * Re-validated against the allowlist on read, so a tampered storage value
 * cannot reach the outbound Connect hub URL.
 *
 * @returns {string | null}
 */
export function getUtmSource() {
  try {
    return normalizeUtmSource(window.sessionStorage.getItem(UTM_SOURCE_KEY));
  } catch {
    return null;
  }
}
